import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, CheckCircle, Quote, ChevronDown, ChevronUp } from 'lucide-react'; 
import DonateBtn from './DonateBtn';

export default function BackToSchool() {
  const [showAll, setShowAll] = useState(false);

  const distributionPhotos = Array.from({ length: 2 }, (_, i) => `/images/bcs${i + 1}.jpeg`);
  const childPhotos = Array.from({ length: 16 }, (_, i) => `/images/bcs${i + 5}.jpeg`);

  const visiblePhotos = showAll ? childPhotos : childPhotos.slice(0, 8);

  return (
    <section id="back-to-school-section" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">

        {/* SECTION 1: THE STORY */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-gh-orange/10 text-gh-orange px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-6">
              < GraduationCap size={16} /> Impact Report: Education
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-gh-blue italic uppercase tracking-tighter leading-[0.9] mb-8">
              Back To School <br />
              <span className="text-gh-orange">Dream Becoming Reality</span>
            </h2>
            <div className="space-y-6 text-slate-600 leading-relaxed text-lg">
              <p>
                At <span className="text-gh-blue font-bold">Giving Hands Charity Ministries</span>, many of our rescued children used to stand and watch others walk to school.
              </p>
              <div className="bg-slate-50 p-6 rounded-2xl border-l-4 border-gh-orange italic flex gap-4">
                <Quote className="text-gh-orange shrink-0" size={32} />
                <p className="font-medium text-gh-blue">
                  "With just a little more help, these children will finally walk into school with the confidence and dignity they have dreamed of for years."
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 gap-4">
            {distributionPhotos.map((src, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                className={`rounded-3xl overflow-hidden shadow-lg border-4 border-white ${i === 1 || i === 2 ? 'mt-8' : ''}`}
              >
                <img src={src} alt="Distribution" className="w-full h-64 object-cover" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* SECTION 2: VOICES OF GRATITUDE */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <h3 className="text-3xl font-black text-gh-blue uppercase italic leading-none">Voices of <span className="text-gh-orange">Gratitude</span></h3>
              <p className="text-gray-400 text-xs tracking-[0.3em] uppercase mt-2 font-bold">Lives Transformed this term</p>
            </div>
            <div className="h-px flex-grow bg-slate-100 mx-8 hidden md:block"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-10">
            <AnimatePresence>
              {visiblePhotos.map((src, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.03 }}
                  className="group relative"
                >
                  <div className="aspect-[3/4] overflow-hidden rounded-2xl border-2 border-slate-50 shadow-sm group-hover:border-gh-orange transition-all duration-300">
                    <img src={src} alt="Happy Child" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-gh-blue/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <CheckCircle size={18} className="text-gh-orange" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="flex flex-col items-center gap-8">
            <button
              onClick={() => setShowAll(!showAll)}
              className="group flex items-center gap-2 bg-slate-50 hover:bg-gh-blue text-gh-blue hover:text-white px-8 py-3 rounded-full font-black text-xs uppercase tracking-widest transition-all duration-300 shadow-sm"
            >
              {showAll ? (
                <>SHOW LESS <ChevronUp size={16} /></>
              ) : (
                <>VIEW ALL FACES <ChevronDown size={16} /></>
              )}
            </button>

            <DonateBtn
              label="Support Education"
              className="!py-4 !px-12 !bg-gh-orange hover:!bg-gh-blue !rounded-full !text-xs !font-black !tracking-[0.2em] shadow-xl transition-all uppercase"
            />
          </div>
        </div>

      </div>
    </section>
  );
}